import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Loader2, LogIn, RefreshCw } from 'lucide-react';
import { ssoService } from '../../api/manpro';
import logoApp from '../../assets/images/logo.png';

type SsoCallbackProps = {
  onLogin: (role: string) => void;
};

const providerDashboardUrl =
  import.meta.env.VITE_PROVIDER_DASHBOARD_URL ||
  'https://indoteknisi.net/provider/dashboard';
const sessionProfileKey = 'manproSessionProfile';

const ssoExpiredMessage =
  'Sesi masuk Bursa Proyek sudah kedaluwarsa. Silakan kembali ke Dashboard Provider dan klik ulang tombol Masuk ke Dashboard Bursa Proyek.';

const normalizeRedirectPath = (path?: string | null) => {
  if (!path || path === '/provider-dashboard' || path === '/dashboard') {
    return '/';
  }

  return path.startsWith('/') ? path : '/';
};

const SsoCallback = ({ onLogin }: SsoCallbackProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasExchangedRef = useRef(false);
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (hasExchangedRef.current) return;
    hasExchangedRef.current = true;

    if (!token) {
      setError(ssoExpiredMessage);
      return;
    }

    const exchangeToken = async () => {
      try {
        // Hapus sisa token dan sesi lama agar tidak dikirim ke backend oleh axios interceptor (menghindari error 401)
        localStorage.removeItem('token');
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem(sessionProfileKey);
        localStorage.removeItem('userRoles');

        // Hapus token dari address bar secepat mungkin agar tidak tertinggal di history/share URL.
        window.history.replaceState(null, '', '/sso/callback');
        // Token dari URL hanya dipakai sekali untuk ditukar menjadi Passport token normal.
        const response = await ssoService.exchangeSsoToken(token);

        const content = response.content;
        const userType = content?.user?.user_type;

        if (response.response_code !== 'default_200' || !content?.token || !userType) {
          setError(ssoExpiredMessage);
          return;
        }

        localStorage.setItem('token', content.token);
        localStorage.setItem('isLoggedIn', 'true');

        if (content.user) {
          localStorage.setItem(sessionProfileKey, JSON.stringify(content.user));
        }

        if (content.user?.roles?.length) {
          localStorage.setItem(
            'userRoles',
            JSON.stringify(content.user.roles.map((role) => role.role_name)),
          );
        }

        onLogin(userType);
        navigate(normalizeRedirectPath(content.redirect_path), { replace: true });
      } catch {
        setError(ssoExpiredMessage);
      }
    };

    exchangeToken();
  }, [navigate, onLogin, token]);

  if (error) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-50 px-4" style={{ zoom: '100%' }}>
        <div className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-xl shadow-slate-200/60">
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-rose-50 text-rose-600">
            <AlertCircle size={32} />
          </div>
          <h1 className="mt-5 text-2xl font-extrabold text-slate-900">Sesi masuk kedaluwarsa</h1>
          <p className="mt-3 text-sm leading-6 text-slate-500">
            {error}
          </p>
          <a
            href={providerDashboardUrl}
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-extrabold text-white transition hover:bg-blue-700"
          >
            <RefreshCw size={17} />
            Kembali ke Dashboard Provider
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-slate-950 to-blue-950 px-4">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/95 p-8 text-center shadow-2xl shadow-blue-950/40">
        <img src={logoApp} alt="Indoteknisi" className="mx-auto h-20 w-20 object-contain" />
        <div className="mt-6 flex justify-center">
          <Loader2 size={36} className="animate-spin text-blue-600" />
        </div>
        <h1 className="mt-5 text-2xl font-extrabold text-slate-900">Membuka Dashboard Bursa Proyek</h1>
        <p className="mt-3 text-sm leading-6 text-slate-500">
          Kami sedang memverifikasi akses dari Dashboard Provider. Anda akan masuk otomatis sebentar lagi.
        </p>
        <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-xs font-extrabold text-blue-700">
          <LogIn size={15} />
          SSO Handoff aktif
        </div>
      </div>
    </div>
  );
};

export default SsoCallback;
